import type { GasPurePayload } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

import type {
	CrossJoinIntegration,
	CrossJoinSourceProduct,
	CrossJoinTargetProduct,
} from '../../common/types';
import type { AccessRequestResponse, AccessRequestType, NonContextualNote } from '../types';

export const requestAccess = async (
	product: CrossJoinTargetProduct,
	sourceProduct: CrossJoinSourceProduct,
	integration: CrossJoinIntegration,
	userId: string,
	cloudId: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	nonContextualNote?: NonContextualNote,
): Promise<AccessRequestType> => {
	const resource = `ari:cloud:${product}::site/${cloudId}`;
	const experimentEligible = nonContextualNote && fg('twcg-1402-non-contextual-ra-note-gate');
	const nonContextualNoteCohort = experimentEligible
		? FeatureGates.getExperimentValue<'variation' | 'control' | 'not-enrolled'>(
				'twcg-1402-non-contextual-ra-note',
				'cohort',
				'not-enrolled',
			)
		: undefined;

	const nonContextualNoteEnabled = nonContextualNoteCohort === 'variation';
	try {
		const response = await fetch('/gateway/api/invitations/v1/access-requests/request', {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				resource,
				...(nonContextualNoteEnabled && { nonContextualNote }),
				metadata: {
					touchpoint: 'Crossjoin Nudge',
					sourceProduct,
				},
			}),
		});

		const accessRequestResponse = (await response.json()) as unknown as AccessRequestResponse;
		const { results } = accessRequestResponse;
		const result = results[resource];
		const analyticsAttributes = {
			userId,
			products: [product],
			inviteResult: [
				{
					resourceAri: resource,
					status: result,
				},
			],
			integration,
			...(experimentEligible && { nonContextualNoteCohort }),
		};

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'requested',
				actionSubject: 'productAccess',
				source: integration,
				attributes: analyticsAttributes,
			} satisfies GasPurePayload,
		}).fire();
		return result;
	} catch (error) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'requestAccess',
				source: integration,
				attributes: {
					message: String(error),
					details: { ...(error || {}) },
				},
			} satisfies GasPurePayload,
		}).fire();
		throw error;
	}
};
